import {
  captureTelegramCheckoutContext,
  hasNativeTelegramBridge,
  hasTelegramCheckoutContext,
  hasTelegramLaunchParameters,
} from './telegramCheckoutContext';

const TELEGRAM_SDK_URL = 'https://telegram.org/js/telegram-web-app.js';
const TELEGRAM_SDK_ID = 'telegram-checkout-sdk';

type TelegramWebApp = {
  initData?: string;
  platform?: string;
  openLink?: (url: string) => void;
  isVersionAtLeast?: (version: string) => boolean;
};
type TelegramWindow = Window & {
  Telegram?: { WebApp?: TelegramWebApp };
};

const activeTelegramWebApp = (): TelegramWebApp | undefined => {
  const webApp = (window as TelegramWindow).Telegram?.WebApp;
  const active = Boolean(webApp?.initData)
    || Boolean(webApp?.platform && webApp.platform !== 'unknown')
    || hasNativeTelegramBridge() || hasTelegramCheckoutContext();
  const transportAvailable = hasNativeTelegramBridge() || window.self !== window.top;
  const supportsExternalLink = !webApp?.isVersionAtLeast || webApp.isVersionAtLeast('6.1');

  return active && transportAvailable && supportsExternalLink
    && typeof webApp?.openLink === 'function' ? webApp : undefined;
};

let sdkLoad: Promise<void> | undefined;

// Ordinary browser pages do not load the SDK: its global object alone is not
// evidence that the page is a Telegram Mini App.
export const loadCheckoutTelegramSdk = (): Promise<void> => {
  if (activeTelegramWebApp()) return Promise.resolve();
  if (sdkLoad && document.getElementById(TELEGRAM_SDK_ID)) return sdkLoad;

  sdkLoad = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.id = TELEGRAM_SDK_ID;
    script.src = TELEGRAM_SDK_URL;
    script.async = true;
    const timeout = window.setTimeout(() => fail(), 5000);
    const cleanup = () => {
      window.clearTimeout(timeout);
      script.onload = null;
      script.onerror = null;
    };
    const fail = () => {
      cleanup();
      script.remove();
      sdkLoad = undefined;
      reject(new Error('Telegram checkout SDK unavailable'));
    };
    script.onload = () => {
      cleanup();
      resolve();
    };
    script.onerror = fail;
    document.head.appendChild(script);
  });

  return sdkLoad;
};

// Telegram's official SDK must read its launch hash before the SPA router can
// remove it. Only actual Telegram launches wait; ordinary /ru and /setup render
// immediately. SDK errors/timeouts still render the app with native-link fallback.
export const initializeTelegramCheckout = (render: () => void): void => {
  captureTelegramCheckoutContext();
  if (!hasTelegramLaunchParameters()) {
    render();
    return;
  }

  void loadCheckoutTelegramSdk().then(render, render);
};

// Must run synchronously in the ready link's user click, never after awaiting
// payment creation: Telegram's external-link event has a short gesture window.
export const openCheckoutWithTelegram = (url: string): boolean => {
  const webApp = activeTelegramWebApp();
  if (!webApp?.openLink) return false;

  try {
    webApp.openLink(url);
    return true;
  } catch {
    return false;
  }
};
